<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const EVENT_LABELS = {
  first_response_time: 'FRT',
  every_response_time: 'ERT',
  resolution_time: 'RT',
};

const PRIORITY_COLORS = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-orange-600',
  urgent: 'text-red-600',
};

function formatCountdown(remainingSeconds) {
  const absSeconds = Math.abs(remainingSeconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const sign = remainingSeconds < 0 ? '-' : '';

  if (hours > 0) return `${sign}${hours}h ${minutes}m`;
  if (minutes > 0) return `${sign}${minutes}m`;
  return `${sign}<1m`;
}

const formattedTime = event => formatCountdown(event.remaining);

const now = ref(Date.now());
const timer = ref(null);

const slaPolicies = computed(() => props.conversation?.slaPolicy || []);
const priority = computed(() => props.conversation?.priority);
const priorityColorClass = computed(
  () => PRIORITY_COLORS[priority.value] || 'text-n-slate-11'
);

const remainingSecondsFor = policy => {
  if (!policy.deadline) return 0;
  return Math.floor((new Date(policy.deadline).getTime() - now.value) / 1000);
};

const visibleEvents = computed(() => {
  const policies = slaPolicies.value;
  if (!policies.length) return [];

  const frt = policies.find(p => p.event === 'first_response_time');
  const ert = policies.find(p => p.event === 'every_response_time');
  const rt = policies.find(p => p.event === 'resolution_time');

  const responseEvent = frt || ert;
  const events = [];

  if (responseEvent) {
    const remaining = remainingSecondsFor(responseEvent);
    const isOverdue = responseEvent.eventResult === 'breached' || remaining < 0;
    events.push({
      label: EVENT_LABELS[responseEvent.event],
      remaining,
      isOverdue,
    });
  }

  const responseOverdue =
    responseEvent &&
    (responseEvent.eventResult === 'breached' ||
      remainingSecondsFor(responseEvent) < 0);

  const showRt =
    rt && (!responseEvent || !responseEvent.deadline || responseOverdue);

  if (showRt) {
    const remaining = remainingSecondsFor(rt);
    const isOverdue = rt.eventResult === 'breached' || remaining < 0;
    events.push({
      label: EVENT_LABELS[rt.event],
      remaining,
      isOverdue,
    });
  }

  return events;
});

const hasSlaThreshold = computed(() => visibleEvents.value.length > 0);

const eventColorClass = isOverdue =>
  isOverdue ? 'text-n-ruby-11' : priorityColorClass.value;

onMounted(() => {
  timer.value = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  if (timer.value) clearInterval(timer.value);
});

watch(
  () => props.conversation,
  () => {
    now.value = Date.now();
  }
);

defineExpose({ hasSlaThreshold });
</script>

<template>
  <div v-if="hasSlaThreshold" class="flex items-center min-w-fit gap-1.5 h-6">
    <span
      v-for="event in visibleEvents"
      :key="event.label"
      class="text-sm truncate font-medium"
      :class="eventColorClass(event.isOverdue)"
    >
      {{ event.label }}: {{ formattedTime(event) }}
    </span>
  </div>
</template>
